import {
  createTheme,
  type MantineColorsTuple,
  Modal,
  Paper,
  Title
} from "@mantine/core";

/* Colors:
   Shades 0-9 in each color tuple were simply generated by taking a color from
   the branding and plugging it into https://mantine.dev/colors-generator/. This
   doesn't result in the color being in the middle of the set nor consistently 
   either end, just sort of wherever the generator felt like based on luminance,
   it seems.  So there's a comment next to whichever one is the actual color in
   the Optima branding guidelines.
   In some cases I've added more colors after that because I wasn't satisfied by
   any of the ones generated and was trying to match something in the hi-fi 
   mockups from UX.  These are noted in comments as well.  (I'm not sold on this
   practice as it makes it impossible to know what ".10" or ".11" means in any
   given color without looking it up here, so we might not continue that.)
*/
const navy: MantineColorsTuple = [
  "#f0f4f9", // #0
  "#e0e5ec", // #1
  "#bdc9db", // #2
  "#97acca", // #3
  "#7793bc", // #4
  "#6383b4", // #5
  "#587bb1", // #6
  "#486a9c", // #7
  "#3e5e8c", // #8
  "#1d304a" // #9, the original color `Navy` from branding
];

const optimaBlue: MantineColorsTuple = [
  "#ebf7ff", // #0
  "#d5ebfa", // #1
  "#a6d6f7", // #2
  "#75bff6", // #3
  "#52acf5", // #4
  "#41a1f5", // #5
  "#379bf6", // #6
  "#2c86db", // #7
  "#2077c4", // #8
  "#00558f" // #9, the original color `Optima Blue` from branding
];

const optimaTeal: MantineColorsTuple = [
  "#ecfbfe", // #0
  "#d9f3f9", // #1
  "#ade7f5", // #2
  "#81dbf1", // #3
  "#63d1ee", // #4
  "#53caec", // #5
  "#49c8ec", // #6
  "#3cb0d2", // #7
  "#2e9dbc", // #8
  "#08819d" // #9, the original color `Optima Teal` from branding
];

const green: MantineColorsTuple = [
  "#eafbf0", // #0
  "#ddf0e4", // #1
  "#bdddc9", // #2
  "#9ac9ac", // #3
  "#7db993", // #4
  "#6aae83", // #5
  "#5aa777", // #6, the original color `Green` from branding
  "#4d9468", // #7
  "#42845b", // #8
  "#32734c" // #9
];

const orange: MantineColorsTuple = [
  "#fff0e8", // #0
  "#fadfd6", // #1
  "#eebeac", // #2
  "#e49a80", // #3
  "#db7c5a", // #4
  "#d66941", // #5
  "#d45f34", // #6
  "#b94d26", // #7, the original color `Optima Orange` from branding
  "#a84420", // #8
  "#943918" // #9
];

const sparkYellow: MantineColorsTuple = [
  "#fff7e2", // #0
  "#fcedcf", // #1
  "#f6d9a3", // #2
  "#efc473", // #3
  "#ebb653", // #4, the original color `Spark Yellow` from branding
  "#e7a730", // #5
  "#e6a120", // #6
  "#cc8c12", // #7
  "#b67c08", // #8
  "#9e6b00", // #9
  "#FDFBF6", // #10, a specific very light shade for background from Christian's design
  "#F8EAC4", // #11, a specific light shade for borders from Christian's design
  "#FFBC3E" // #12, a specific vibrant shade for nav items from Christian's design
];

const lightTeal: MantineColorsTuple = [
  "#e4fbff", // #0
  "#d6f1f6", // #1
  "#b2e0e8", // #2
  "#8fd0db", // #3, the original color `Light Teal` from branding
  "#69bfcd", // #4
  "#54b5c6", // #5
  "#45b1c3", // #6
  "#339bac", // #7
  "#228a9b", // #8
  "#007888" // #9
];

const lightBlue: MantineColorsTuple = [
  "#e3faff", // #0
  "#d2f0fb", // #1
  "#b8e5f5", // #2, the original color `Light Blue` from branding
  "#7acdeb", // #3
  "#56bee4", // #4
  "#3fb4e0", // #5
  "#2eb0e0", // #6
  "#1c9ac7", // #7
  "#0089b3", // #8
  "#00779e" // #9
];

const warmGray: MantineColorsTuple = [
  "#ecf7f4", // #0
  "#e4e8e7", // #1
  "#cdcecd", // #2
  "#b2b2b2", // #3
  "#9b9b9b", // #4
  "#8c8c8c", // #5
  "#848585", // #6
  "#707372", // #7, the original color `Warm Gray` from branding
  "#616765", // #8
  "#4e5a56" // #9
];

const globalTheme = createTheme({
  primaryColor: "optimaBlue",
  primaryShade: 9,
  colors: {
    navy,
    optimaBlue,
    optimaTeal,
    green,
    orange,
    sparkYellow,
    lightTeal,
    lightBlue,
    warmGray
  },
  fontFamily:
    'Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji","Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  shadows: {
    xs: "0 2px 3px rgba(0, 0, 0, 0.1)",
    md: "0 8px 24px rgba(0, 0, 0, .06)"
  },
  components: {
    Paper: Paper.extend({
      defaultProps: {
        shadow: "xs",
        withBorder: true
      }
    }),
    Title: Title.extend({
      defaultProps: {
        c: "optimaBlue.9",
        mt: 20,
        mb: 10
      }
    }),
    Modal: Modal.extend({
      styles: {
        content: {
          paddingBlock: "0.5rem",
          paddingInline: "1rem",
          // padding: "1rem",
          // paddingTop: "0.75rem",
          borderRadius: "0.6rem"
        },
        header: {
          marginBottom: "0.4rem"
        },
        title: {
          color: "var(--mantine-color-optimaBlue-9)",
          fontSize: "1.4rem"
        }
      }
    })
  }
});

export default globalTheme;
